import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { hasPermission } from '../utils/permissions';

export default function BulkDelete() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchPreview = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const res = await api.get('/admin/bulk-delete/preview');
      setItems(res.data || []);
    } catch (e) {
      setErrorMessage(
        e.response?.data?.detail || 'Failed to load collection counts'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasPermission(user, 'bulk_delete', 'view')) {
      fetchPreview();
    }
  }, [user, fetchPreview]);

  const toggle = (key) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedKeys = items.filter((i) => selected[i.key]).map((i) => i.key);
  const selectedTotal = items
    .filter((i) => selected[i.key])
    .reduce((sum, i) => sum + i.count, 0);

  const handleDelete = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await api.post('/admin/bulk-delete', {
        collections: selectedKeys,
        confirm_text: 'DELETE',
      });
      setResultMessage(
        `Successfully deleted ${res.data.total} record(s) across ${selectedKeys.length} collection(s).`
      );
      setShowConfirm(false);
      setConfirmText('');
      setSelected({});
      await fetchPreview();
    } catch (e) {
      setErrorMessage(
        e.response?.data?.detail || 'Bulk delete failed. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission(user, 'bulk_delete', 'view')) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-700" data-testid="bulk-delete-no-access">
              Admin access required to view this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="bulk-delete-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          Bulk Delete
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Permanently clear transactional data from selected collections. This
          action cannot be undone.
        </p>
      </div>

      <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold mb-1">⚠️ Danger zone</p>
        <p>
          Deletions are permanent. Users, teams, master data and company
          settings are <strong>not</strong> affected by this tool.
        </p>
      </div>

      {resultMessage && (
        <div
          className="rounded-md border border-green-300 bg-green-50 p-4 text-sm text-green-800"
          data-testid="bulk-delete-success"
        >
          {resultMessage}
        </div>
      )}

      {errorMessage && (
        <div
          className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800"
          data-testid="bulk-delete-error"
        >
          {errorMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Select collections to clear</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <label
                  key={item.key}
                  className="flex items-center justify-between gap-4 p-3 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  data-testid={`bulk-delete-row-${item.key}`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={!!selected[item.key]}
                      onChange={() => toggle(item.key)}
                      disabled={item.count === 0}
                      className="h-4 w-4"
                      data-testid={`bulk-delete-checkbox-${item.key}`}
                    />
                    <span className="font-medium text-gray-900">
                      {item.label}
                    </span>
                  </div>
                  <span
                    className={`text-sm ${
                      item.count === 0 ? 'text-gray-400' : 'text-gray-700'
                    }`}
                    data-testid={`bulk-delete-count-${item.key}`}
                  >
                    {item.count.toLocaleString()} record
                    {item.count === 1 ? '' : 's'}
                  </span>
                </label>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-600">
          {selectedKeys.length} collection
          {selectedKeys.length === 1 ? '' : 's'} selected ·{' '}
          <span className="font-semibold text-red-700">
            {selectedTotal.toLocaleString()}
          </span>{' '}
          record{selectedTotal === 1 ? '' : 's'} will be deleted
        </p>
        <Button
          onClick={() => {
            setResultMessage(null);
            setErrorMessage(null);
            setShowConfirm(true);
          }}
          disabled={selectedKeys.length === 0 || selectedTotal === 0}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="bulk-delete-open-confirm-btn"
        >
          Delete selected
        </Button>
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="bulk-delete-confirm-modal"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-700">Confirm deletion</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-700">
                You are about to permanently delete{' '}
                <strong className="text-red-700">
                  {selectedTotal.toLocaleString()}
                </strong>{' '}
                record(s) from:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
                {items
                  .filter((i) => selected[i.key])
                  .map((i) => (
                    <li key={i.key}>
                      {i.label} ({i.count.toLocaleString()})
                    </li>
                  ))}
              </ul>
              <div>
                <label className="text-sm text-gray-700">
                  Type{' '}
                  <code className="px-1 py-0.5 bg-gray-100 rounded">
                    DELETE
                  </code>{' '}
                  to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="DELETE"
                  data-testid="bulk-delete-confirm-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirm(false);
                    setConfirmText('');
                  }}
                  disabled={submitting}
                  data-testid="bulk-delete-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={confirmText !== 'DELETE' || submitting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="bulk-delete-confirm-btn"
                >
                  {submitting ? 'Deleting…' : 'Permanently delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
